// SPDX-License-Identifier: Apache-2.0

// camera-rig.ts — port of v1 vault-orbit.tsx:1262-1283 CameraRig component.
//
// v1 used r3f's `useFrame({ mouse, clock })`. In v2 imperative land we read
// pointer NDC from the canvas listener and elapsed time from a clock the
// caller passes in. Same lerp shape:
//
//   target = base + (mouse.x * 0.6, mouse.y * 0.35 + sin(t * 0.3) * 0.08,
//                    cos(t * 0.22) * 0.12)
//   camera.position.lerp(target, 0.06)
//
// The base position scales with doc count so 100-doc graphs don't look
// claustrophobic — pulled straight from v1's switch statement.

import * as THREE from "three";

export interface CameraRigState {
  /** Pointer position in normalized device coords (-1..1, -1..1). */
  pointer: THREE.Vector2;
  /** Whether the pointer is currently within the canvas. */
  pointerActive: boolean;
}

export class CameraRig {
  private camera: THREE.PerspectiveCamera;
  private base: [number, number, number] = [0, 1.6, 7.5];
  private tmp = new THREE.Vector3();
  private state: CameraRigState;
  /** External takeover (e.g. PointerLock embodied controller). When true,
   *  this rig stops touching `camera.position`. Apollo's stream wires this. */
  takeover = false;

  constructor(camera: THREE.PerspectiveCamera, state: CameraRigState) {
    this.camera = camera;
    this.state = state;
  }

  /** Recompute base position from doc count. v1's exact tiers. */
  setDocCount(n: number): void {
    if (n > 48) this.base = [0, 2.6, 14];
    else if (n > 24) this.base = [0, 2.2, 11];
    else if (n > 8) this.base = [0, 1.9, 9];
    else this.base = [0, 1.6, 7.5];
  }

  /** Per-frame update. Lerps camera toward (base + mouse-driven offset). */
  update(elapsedSec: number): void {
    if (this.takeover) return;
    const [bx, by, bz] = this.base;
    const mx = this.state.pointerActive ? this.state.pointer.x : 0;
    const my = this.state.pointerActive ? this.state.pointer.y : 0;
    this.tmp.set(
      bx + mx * 0.6,
      by + my * 0.35 + Math.sin(elapsedSec * 0.3) * 0.08,
      bz + Math.cos(elapsedSec * 0.22) * 0.12,
    );
    this.camera.position.lerp(this.tmp, 0.06);
    this.camera.lookAt(0, 0, 0);
  }
}
